import { normaliseState } from 'components/Signup/helpers/stateNormalisation';
import {
  useGetOnlineAccounts,
  useGetStateContext,
  useGetStateQuery,
} from 'lendingApi/lendingApiHooks';

type TFormattedLinkedBankAccount = {
  bankName: string;
  accountNumberLastFourDigits: string;
};

export const useLinkOpenBankingStatus = () => {
  const { state, stateFetching } = useGetStateQuery();
  const stateKey = state?.state_key;
  const {
    data: stateContextResponse,
    isLoading: stateContextLoading,
    isError: stateContextError,
  } = useGetStateContext(stateKey);

  const {
    data: onlineAccountsResponse,
    isLoading: onlineAccountsLoading,
    isError: onlineAccountsError,
  } = useGetOnlineAccounts(stateKey);

  const { applicant } = normaliseState(state);

  const linkedBankAccounts: TFormattedLinkedBankAccount[] = [];

  if (stateContextResponse?.data && onlineAccountsResponse?.data) {
    const stateContext = stateContextResponse.data;
    const onlineAccounts = onlineAccountsResponse.data;

    const openBankingProviders = [
      ...(stateContext.open_banking?.banks ?? []),
      ...(stateContext.open_banking.truelayer_banks ?? []),
    ];

    const providerNames = openBankingProviders.map(
      (provider) => provider.bank_name,
    );

    const accountsInOpenBankingProviders = onlineAccounts.filter(
      ({ provider }) => providerNames.includes(provider),
    );

    const activeAccounts = accountsInOpenBankingProviders.filter(
      ({ status }) => status?.code === 'active',
    );

    const enrichedAccounts = activeAccounts.map((account) => {
      const bank = openBankingProviders.find(
        ({ bank_name }) => bank_name === account.provider,
      );
      return { ...account, bank_name: bank?.bank_name || 'Unknown' };
    });

    const linkedBanks = enrichedAccounts.map((account) => ({
      bankName: account.bank_name,
      accounts: account.accounts.map(({ extra = {} }) => ({
        balance: extra.balance,
        accountNumber: extra.account_number,
      })),
    }));

    for (const { bankName, accounts } of linkedBanks) {
      for (const account of accounts) {
        linkedBankAccounts.push({
          bankName,
          accountNumberLastFourDigits: (account.accountNumber as string).slice(
            -4,
          ),
        });
      }
    }
  }

  return {
    linkedBankAccounts,
    isFetchingLinkedBankAccounts:
      stateFetching || stateContextLoading || onlineAccountsLoading,
    isError: stateContextError || onlineAccountsError,
    customerEmail: applicant?.email,
    customerFirstName: applicant?.firstName,
  };
};
